import { useState, useMemo } from 'react'
import { useFinance } from '../../context/FinanceContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency, formatPercent } from '../../lib/formatters'
import { STATE_TAX_DATA } from '../../lib/calculations'
import { calculateCapitalGains } from '../../lib/calculations/capitalGains'
import { trackExportReport } from '../../lib/analytics'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { useSubscription } from '../../context/SubscriptionContext'

function InputField({ label, value, onChange, prefix, suffix, type = 'number', step, min, max, hint }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">{prefix}</span>}
        <input
          type={type}
          value={value}
          onChange={e => onChange(type === 'number' ? (parseFloat(e.target.value) || 0) : e.target.value)}
          step={step}
          min={min}
          max={max}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:border-primary transition ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-8' : 'pr-3'}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-faint text-sm">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-faint mt-1">{hint}</p>}
    </div>
  )
}

function ResultCard({ label, value, color, highlight }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
    </GlassCard>
  )
}

export default function CapitalGainsTax() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)

  const [purchasePrice, setPurchasePrice] = useState(10000)
  const [salePrice, setSalePrice] = useState(25000)
  const [holdingPeriodMonths, setHoldingPeriodMonths] = useState(18)
  const [filingStatus, setFilingStatus] = useState('single')
  const [otherIncome, setOtherIncome] = useState(75000)
  const [stateCode, setStateCode] = useState('OH')

  const result = useMemo(() => calculateCapitalGains({
    purchasePrice, salePrice, holdingPeriodMonths, filingStatus, otherIncome, stateCode,
  }), [purchasePrice, salePrice, holdingPeriodMonths, filingStatus, otherIncome, stateCode])

  const filingStatusLabels = { single: 'Single', mfj: 'Married Filing Jointly', hoh: 'Head of Household' }

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 14
    const contentWidth = pageWidth - margin * 2

    const checkPageBreak = (y, needed = 40) => {
      if (y + needed > pageHeight - margin) {
        doc.addPage()
        return margin + 4
      }
      return y
    }

    // ── Title ──
    doc.setFillColor(255, 255, 255)
    doc.rect(0, 0, pageWidth, pageHeight, 'F')
    doc.setFontSize(22)
    doc.setTextColor(30, 30, 30)
    doc.text('Capital Gains Tax Report', margin, 22)
    doc.setFontSize(10)
    doc.setTextColor(120, 120, 120)
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}`, margin, 30)
    doc.setDrawColor(200, 200, 200)
    doc.line(margin, 33, pageWidth - margin, 33)

    // ── Section 1: Investment Details (Input Summary) ──
    let y = 40
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text('Investment Details', margin, y)
    y += 4

    const stateEntry = STATE_TAX_DATA.find(s => s.code === stateCode)
    const stateName = stateEntry ? `${stateEntry.name} (${stateEntry.rate}%)` : stateCode

    autoTable(doc, {
      startY: y,
      head: [['Input', 'Value']],
      body: [
        ['Purchase Price (Cost Basis)', formatCurrency(purchasePrice)],
        ['Sale Price', formatCurrency(salePrice)],
        ['Capital Gain / (Loss)', result.gain > 0 ? formatCurrency(result.gain) : result.loss > 0 ? `(${formatCurrency(result.loss)})` : '$0'],
        ['Holding Period', `${holdingPeriodMonths} month${holdingPeriodMonths !== 1 ? 's' : ''}`],
        ['Gain Classification', result.isLongTerm ? 'Long-Term (12+ months)' : 'Short-Term (< 12 months)'],
        ['Other Annual Income', formatCurrency(otherIncome)],
        ['Filing Status', filingStatusLabels[filingStatus] || filingStatus],
        ['State', stateName],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 9.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
    })

    // ── Section 2: Key Results ──
    y = (doc.lastAutoTable?.finalY || 90) + 12
    y = checkPageBreak(y, 50)
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text('Key Results', margin, y)
    y += 4

    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Capital Gain', formatCurrency(result.gain)],
        ['Total Tax Owed', formatCurrency(result.actualTotalTax)],
        ['Effective Tax Rate on Gain', formatPercent(result.effectiveRate)],
        ['Net Proceeds (Sale Price - Tax)', formatCurrency(result.netProceeds)],
      ],
      theme: 'grid',
      headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 9.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
    })

    // ── Section 3: Your Tax Breakdown (Actual) ──
    y = (doc.lastAutoTable?.finalY || 130) + 12
    y = checkPageBreak(y, 50)
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text(`Your Tax Breakdown (${result.isLongTerm ? 'Long-Term' : 'Short-Term'})`, margin, y)
    y += 4

    const actualBreakdownRows = [
      ['Federal Tax', formatCurrency(result.isLongTerm ? result.longTermFederalTax : result.shortTermFederalTax)],
      [`State Tax (${result.stateRate}%)`, formatCurrency(result.isLongTerm ? result.longTermStateTax : result.shortTermStateTax)],
    ]
    if (result.actualNIIT > 0) {
      actualBreakdownRows.push(['Net Investment Income Tax (3.8%)', formatCurrency(result.actualNIIT)])
    }
    actualBreakdownRows.push(['Total Tax', formatCurrency(result.actualTotalTax)])

    autoTable(doc, {
      startY: y,
      head: [['Tax Component', 'Amount']],
      body: actualBreakdownRows,
      theme: 'grid',
      headStyles: { fillColor: [239, 68, 68], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 9.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
    })

    // ── Section 4: Short-Term vs Long-Term Comparison ──
    y = (doc.lastAutoTable?.finalY || 170) + 12
    y = checkPageBreak(y, 70)
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text('Short-Term vs Long-Term Comparison', margin, y)
    y += 4

    const comparisonRows = [
      ['Federal Tax', formatCurrency(result.shortTermFederalTax), formatCurrency(result.longTermFederalTax)],
      [`State Tax (${result.stateRate}%)`, formatCurrency(result.shortTermStateTax), formatCurrency(result.longTermStateTax)],
    ]
    if (result.shortTermNIIT > 0 || result.longTermNIIT > 0) {
      comparisonRows.push(['NIIT (3.8%)', formatCurrency(result.shortTermNIIT), formatCurrency(result.longTermNIIT)])
    }
    comparisonRows.push(
      ['Total Tax', formatCurrency(result.shortTermTotalTax), formatCurrency(result.longTermTotalTax)],
      ['Net Proceeds', formatCurrency(salePrice - result.shortTermTotalTax), formatCurrency(salePrice - result.longTermTotalTax)],
    )

    autoTable(doc, {
      startY: y,
      head: [[
        'Tax Component',
        `Short-Term${!result.isLongTerm ? ' (Current)' : ''}`,
        `Long-Term${result.isLongTerm ? ' (Current)' : ''}`,
      ]],
      body: comparisonRows,
      theme: 'striped',
      headStyles: { fillColor: [139, 92, 246], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 9.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
      columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right' },
      },
    })

    // ── NIIT Details (if applicable) ──
    if (result.shortTermNIIT > 0 || result.longTermNIIT > 0 || result.actualNIIT > 0) {
      y = (doc.lastAutoTable?.finalY || 200) + 10
      y = checkPageBreak(y, 30)
      doc.setFontSize(11)
      doc.setTextColor(100, 60, 160)
      doc.text('Net Investment Income Tax (NIIT) Details', margin, y)
      y += 6
      doc.setFontSize(9)
      doc.setTextColor(80, 80, 80)
      const niitLines = doc.splitTextToSize(
        'The 3.8% NIIT applies to investment income when your modified adjusted gross income (MAGI) exceeds the threshold for your filing status. ' +
        `Your MAGI of ${formatCurrency(otherIncome + result.gain)} ${(otherIncome + result.gain) > (filingStatus === 'mfj' ? 250000 : 200000) ? 'exceeds' : 'is below'} the ${formatCurrency(filingStatus === 'mfj' ? 250000 : 200000)} threshold.`,
        contentWidth
      )
      doc.text(niitLines, margin, y)
      y += niitLines.length * 4.5
    }

    // ── Tax Savings Banner ──
    y = (doc.lastAutoTable?.finalY || y) + 10
    y = checkPageBreak(y, 30)

    if (result.savings > 0) {
      doc.setFillColor(240, 253, 244)
      doc.roundedRect(margin, y, contentWidth, 18, 3, 3, 'F')
      doc.setDrawColor(34, 197, 94)
      doc.roundedRect(margin, y, contentWidth, 18, 3, 3, 'S')
      doc.setFontSize(11)
      doc.setTextColor(22, 101, 52)
      doc.text(
        `Holding for 12+ months would save ${formatCurrency(result.savings)} in taxes on this gain.`,
        margin + 6, y + 11
      )
      y += 24
    } else if (result.savings === 0 && result.gain > 0) {
      doc.setFillColor(248, 250, 252)
      doc.roundedRect(margin, y, contentWidth, 14, 3, 3, 'F')
      doc.setFontSize(9.5)
      doc.setTextColor(80, 80, 80)
      doc.text('Short-term and long-term tax amounts are the same for this income level.', margin + 6, y + 9)
      y += 20
    }

    // ── Disclaimer ──
    y = checkPageBreak(y, 30)
    doc.setFontSize(8)
    doc.setTextColor(150, 150, 150)
    const disclaimerLines = doc.splitTextToSize(
      'Disclaimer: This report is for informational purposes only and does not constitute tax advice. Capital gains tax calculations are estimates based on 2025 federal tax brackets and state flat rates. Actual tax liability may vary based on your complete tax situation. Consult a qualified tax professional for personalized advice.',
      contentWidth
    )
    doc.text(disclaimerLines, margin, y)

    doc.save(`capital-gains-tax-${Date.now()}.pdf`)
  }

  const filingOptions = [
    { value: 'single', label: 'Single' },
    { value: 'mfj', label: 'MFJ' },
    { value: 'hoh', label: 'HOH' },
  ]

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="text-lg font-semibold text-heading">Investment Details</h3>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InputField label="Purchase Price" value={purchasePrice} onChange={setPurchasePrice} prefix="$" />
          <InputField label="Sale Price" value={salePrice} onChange={setSalePrice} prefix="$" />
          <InputField label="Holding Period" value={holdingPeriodMonths} onChange={setHoldingPeriodMonths} suffix="mo" min={1} hint={result.isLongTerm ? 'Long-term (12+ months)' : 'Short-term (< 12 months)'} />
          <InputField label="Other Annual Income" value={otherIncome} onChange={setOtherIncome} prefix="$" />
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Filing Status</label>
            <div className="flex gap-1">
              {filingOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFilingStatus(opt.value)}
                  className={`flex-1 px-2 py-2 rounded-xl text-xs font-medium transition-all ${
                    filingStatus === opt.value ? 'text-heading shadow-sm' : 'text-muted hover:text-heading'
                  }`}
                  style={filingStatus === opt.value ? {
                    background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                    border: `1px solid ${colors.primary}44`,
                  } : { border: '1px solid transparent' }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">State</label>
            <select
              value={stateCode}
              onChange={e => setStateCode(e.target.value)}
              className="w-full bg-input border border-input rounded-xl py-2.5 px-3 text-sm text-heading focus:outline-none focus:border-primary transition"
            >
              {STATE_TAX_DATA.filter(s => s.code !== 'CUSTOM').map(s => (
                <option key={s.code} value={s.code}>{s.name} ({s.rate}%)</option>
              ))}
            </select>
          </div>
        </div>
      </GlassCard>

      {/* Key Results */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ResultCard label="Capital Gain" value={formatCurrency(result.gain)} color={colors.positive} highlight />
        <ResultCard label="Total Tax" value={formatCurrency(result.actualTotalTax)} color={colors.negative} highlight />
        <ResultCard label="Effective Rate" value={formatPercent(result.effectiveRate)} color={colors.primary} />
        <ResultCard label="Net Proceeds" value={formatCurrency(result.netProceeds)} color={colors.positive} highlight />
      </div>

      {/* Short vs Long Comparison */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Short-Term vs Long-Term Comparison</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className={`p-4 rounded-xl border ${!result.isLongTerm ? 'border-primary/30 bg-primary/5' : 'border-card'}`}>
            <div className="flex items-center gap-2 mb-3">
              <h4 className="text-sm font-semibold text-heading">Short-Term</h4>
              {!result.isLongTerm && <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">Current</span>}
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted">Federal Tax</span><span className="text-heading">{formatCurrency(result.shortTermFederalTax)}</span></div>
              <div className="flex justify-between"><span className="text-muted">State Tax</span><span className="text-heading">{formatCurrency(result.shortTermStateTax)}</span></div>
              {result.shortTermNIIT > 0 && <div className="flex justify-between"><span className="text-muted">NIIT (3.8%)</span><span className="text-heading">{formatCurrency(result.shortTermNIIT)}</span></div>}
              <div className="border-t border-card pt-2 flex justify-between font-semibold">
                <span className="text-heading">Total</span>
                <span style={{ color: colors.negative }}>{formatCurrency(result.shortTermTotalTax)}</span>
              </div>
            </div>
          </div>

          <div className={`p-4 rounded-xl border ${result.isLongTerm ? 'border-primary/30 bg-primary/5' : 'border-card'}`}>
            <div className="flex items-center gap-2 mb-3">
              <h4 className="text-sm font-semibold text-heading">Long-Term</h4>
              {result.isLongTerm && <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">Current</span>}
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted">Federal Tax</span><span className="text-heading">{formatCurrency(result.longTermFederalTax)}</span></div>
              <div className="flex justify-between"><span className="text-muted">State Tax</span><span className="text-heading">{formatCurrency(result.longTermStateTax)}</span></div>
              {result.longTermNIIT > 0 && <div className="flex justify-between"><span className="text-muted">NIIT (3.8%)</span><span className="text-heading">{formatCurrency(result.longTermNIIT)}</span></div>}
              <div className="border-t border-card pt-2 flex justify-between font-semibold">
                <span className="text-heading">Total</span>
                <span style={{ color: colors.negative }}>{formatCurrency(result.longTermTotalTax)}</span>
              </div>
            </div>
          </div>
        </div>

        {result.savings > 0 && (
          <div className="mt-4 p-3 rounded-xl" style={{ background: `${colors.positive}15`, border: `1px solid ${colors.positive}33` }}>
            <p className="text-sm" style={{ color: colors.positive }}>
              Holding for 12+ months saves <strong>{formatCurrency(result.savings)}</strong> in taxes on this gain.
            </p>
          </div>
        )}
      </GlassCard>
    </div>
  )
}
